import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../lib/i18n";
import { STATUS_STYLE } from "./SprayOrders";
import { compatWarnings, rateOutOfRange, LABEL_DB_URL, labelSearchUrl } from "./compat";
import { Field } from "../scouting/parts";
import { ArrowLeft, Printer, CheckCircle2, ShieldAlert, Loader2, X, Ban, ThumbsUp, Eye, Download, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

export default function FieldSheet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const isManager = ["admin", "supervisor"].includes(user?.role);
  const [o, setO] = useState(null);
  const [busy, setBusy] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => setO((await api.get(`/pest/spray-orders/${id}`)).data);
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  if (!o) return <div className="p-8 font-bold uppercase text-sm">{t("fs.loading")}</div>;

  const act = async (action) => {
    setBusy(true); setErr("");
    try { setO((await api.post(`/pest/spray-orders/${id}/action`, { action })).data); }
    catch (e) { setErr(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const downloadPdf = async (lang = "en") => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/pest/spray-orders/${id}/pdf?lang=${lang}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${o.wo_number}-field-sheet${lang === "es" ? "-ES" : ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setErr("Could not download the PDF"); }
    finally { setPdfBusy(false); }
  };

  const calc = o.calc || {};
  const prods = calc.products || [];
  const maxRei = Math.max(0, ...prods.map((p) => p.rei_hours || 0));
  const maxPhi = Math.max(0, ...prods.map((p) => p.phi_days || 0));
  const unit = (p) => p.rate_unit || "";
  const amtUnit = (p) => (p.rate_unit || "").replace(/\s*\/\s*(ac|acre)s?\s*$/i, "");

  return (
    <div className="space-y-4 max-w-3xl" data-testid="field-sheet">
      <style>{`@media print { header, aside, footer, .no-print, nav { display: none !important; } main { padding: 0 !important; } }`}</style>

      <div className="no-print flex flex-wrap items-center justify-between gap-2">
        <button onClick={() => navigate("/pest/orders")} className="text-xs font-bold uppercase text-stone-500 flex items-center gap-1"><ArrowLeft size={13} /> {t("fs.back")}</button>
        <div className="flex gap-2">
          <button data-testid="download-pdf-btn" onClick={() => downloadPdf("en")} disabled={pdfBusy} className="h-10 px-4 bg-[#1F3D34] text-white font-bold uppercase text-xs flex items-center gap-2 disabled:opacity-40">
            {pdfBusy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} PDF
          </button>
          <button data-testid="download-pdf-es-btn" onClick={() => downloadPdf("es")} disabled={pdfBusy} className="h-10 px-4 bg-[#7C9673] text-white font-bold uppercase text-xs flex items-center gap-2 disabled:opacity-40">
            {pdfBusy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} PDF Español
          </button>
          <button data-testid="rei-sheet-btn" onClick={async () => {
            const res = await api.get(`/pest/spray-orders/${id}/rei-sheet`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a"); a.href = url; a.download = `REI-${o.wo_number}.pdf`;
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
          }} className="h-10 px-4 bg-red-700 text-white font-bold uppercase text-xs flex items-center gap-2">
            <ShieldAlert size={14} /> REI Sheet
          </button>
          {o.status === "completed" && ["admin", "supervisor"].includes(user?.role) && (
            <button data-testid="create-pur-btn" onClick={async () => {
              await api.post(`/pest/pur-records/from-order/${id}`);
              toast.success("PUR record created — review it under PUR Records on the Spray Orders page");
            }} className="h-10 px-4 border border-[#1F3D34] text-[#1F3D34] bg-white font-bold uppercase text-xs flex items-center gap-2">
              <FileCheck2 size={14} /> Create PUR
            </button>
          )}
          <button data-testid="print-btn" onClick={() => window.print()} className="h-10 px-4 border border-stone-300 bg-white font-bold uppercase text-xs flex items-center gap-2"><Printer size={14} /> {t("fs.print")}</button>
        </div>
      </div>

      {/* Header */}
      <div className="bg-[#1F3D34] text-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="font-mono text-2xl font-bold" data-testid="wo-number">{o.wo_number}</div>
            <div className="text-white/70 text-sm">{t("fs.wo")} — {o.target}</div>
          </div>
          <span className={`text-xs font-bold uppercase px-3 py-1.5 ${STATUS_STYLE[o.status] || "bg-stone-200 text-stone-700"}`} data-testid="wo-status">{o.status}</span>
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Info dark label={t("fs.client")} v={o.client_name} />
          <Info dark label={t("fs.vineyard")} v={`${o.ranch_number} · ${o.vineyard_name}`} />
          <Info dark label={t("fs.date")} v={`${o.date || "TBD"}${o.planned_start ? ` · ${o.planned_start}` : ""}`} />
          <Info dark label={t("fs.treated")} v={`${o.treated_acres} ac`} />
        </div>
      </div>

      {/* Recommended banner */}
      {o.status === "recommended" && (
        <div className="bg-amber-50 border-2 border-amber-500 p-4 text-sm" data-testid="recommended-banner">
          <div className="font-bold uppercase text-amber-800">Recommended from field scouting</div>
          <div className="text-amber-900 mt-1">{o.reason}{o.scout_note ? ` — "${o.scout_note}"` : ""}</div>
          <div className="text-xs text-amber-800 mt-1">A manager selects materials, tank size and row pattern at approval before this is released to the field.</div>
        </div>
      )}

      {/* Blocks */}
      <Panel title={t("fs.blocks")}>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[10px] uppercase text-stone-500"><th className="py-1">{t("fs.block")}</th><th>{t("fs.variety")}</th><th className="text-right">{t("fs.acres")}</th></tr></thead>
          <tbody>
            {(o.blocks || []).map((b) => (
              <tr key={b.id} className="border-t border-stone-100"><td className="py-1.5 font-semibold">{b.block}</td><td>{b.variety || "—"}</td><td className="text-right">{b.acres ?? "—"}</td></tr>
            ))}
          </tbody>
        </table>
        {o.reason && <div className="text-xs text-stone-600 mt-1">Why: {o.reason}</div>}
      </Panel>

      {/* Pattern */}
      <div className={`p-4 border-2 ${o.pattern === "every_other_row" ? "border-amber-500 bg-amber-50" : "border-stone-200 bg-white"}`} data-testid="pattern-panel">
        <div className="font-bold uppercase text-sm">
          {o.pattern === "every_other_row" ? t("fs.eor") : o.pattern === "custom" ? t("fs.custom_rows") : t("fs.every_row")}
        </div>
        {o.pattern === "every_other_row" && (
          <div className="text-sm mt-1 font-semibold">Start at <b>{o.start_row}</b>{o.direction ? ` — ${o.direction}` : ""}. Skip alternate rows. Treated acres are half of block acres.</div>
        )}
        {o.pattern === "custom" && o.row_numbers && <div className="text-sm mt-1 font-semibold">Rows: {o.row_numbers}</div>}
      </div>

      {/* People & Equipment */}
      <Panel title={t("fs.people")}>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <Info label={t("fs.applicator")} v={o.applicator} />
          <Info label={t("fs.manager")} v={o.manager} />
          <Info label={t("fs.supervisor")} v={o.supervisor} />
          <Info label="PCA" v={o.pca} />
          <Info label={t("fs.tractor")} v={o.tractor} />
          <Info label={t("fs.sprayer")} v={o.sprayer} />
          <Info label={t("fs.method")} v={o.method} />
          <Info label={t("fs.nozzles")} v={o.nozzle_setup ? `${o.nozzle_setup}${o.nozzle_count ? ` × ${o.nozzle_count}` : ""}` : null} />
          <Info label={t("fs.county")} v={[o.county, o.site_permit_id].filter(Boolean).join(" · ") || null} />
        </div>
      </Panel>

      {/* Calibration */}
      <Panel title={t("fs.calibration")} testId="calibration-panel">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Info label={t("fs.target_gpa")} v={o.gpa} />
          <Info label={t("fs.speed")} v={o.speed_mph ? `${o.speed_mph} MPH` : null} />
          <Info label={t("fs.pressure")} v={o.pressure_psi ? `${o.pressure_psi} PSI` : null} />
          <Info label={t("fs.output")} v={o.sprayer_gpm ? `${o.sprayer_gpm} GPM` : null} />
        </div>
        {calc.calibration && (
          <div className={`mt-2 text-xs p-3 border font-semibold ${calc.calibration.mismatch ? "bg-red-50 border-red-400 text-red-800" : "bg-[#7C9673]/10 border-[#7C9673] text-[#1F3D34]"}`} data-testid="calibration-note">
            {calc.calibration.formula} = <b>{calc.calibration.calculated_gpa} GPA</b>. {calc.calibration.note}
          </div>
        )}
      </Panel>

      {/* Tank table */}
      <Panel title={`${t("fs.tank_loads")} — ${calc.tanks_label || ""}`} testId="tank-table-panel">
        <div className="text-xs text-stone-600 mb-2">{o.treated_acres} ac × {o.gpa} GPA = <b>{(calc.total_volume_gal || 0).toLocaleString()} gal</b> total · {calc.acres_per_tank} ac per full tank</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-stone-200">
            <thead>
              <tr className="bg-stone-100 text-left text-[10px] uppercase text-stone-600">
                <th className="p-2">Material</th>
                <th className="p-2">Per Full Tank — {o.tank_gal} gal{calc.full_tanks > 0 ? ` × ${calc.full_tanks}` : ""}</th>
                {calc.final_tank_gal > 0 && <th className="p-2">Partial Tank — {calc.final_tank_gal} gal</th>}
                <th className="p-2">Total Needed</th>
              </tr>
            </thead>
            <tbody>
              {prods.map((p) => (
                <tr key={p.material_id} className="border-t border-stone-200" data-testid={`tank-row-${p.material_id}`}>
                  <td className="p-2 font-bold">{p.product}<span className="block text-[10px] font-normal text-stone-500">{p.rate ? `${p.rate} ${unit(p)}` : "rate TBD at approval"}</span></td>
                  <td className="p-2 font-mono font-bold">{p.rate ? `${p.per_tank ?? "—"} ${amtUnit(p)}` : "TBD"}</td>
                  {calc.final_tank_gal > 0 && <td className="p-2 font-mono font-bold bg-amber-50">{p.rate ? `${p.final_tank_amount ?? "—"} ${amtUnit(p)}` : "TBD"}</td>}
                  <td className="p-2 font-mono">{p.rate ? `${p.total_required} ${amtUnit(p)}` : "TBD"}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-stone-300 bg-stone-50" data-testid="tank-row-water">
                <td className="p-2 font-bold uppercase text-[10px]">Water</td>
                <td className="p-2 font-mono">{o.tank_gal} gal each</td>
                {calc.final_tank_gal > 0 && <td className="p-2 font-mono bg-amber-50">{calc.final_tank_gal} gal</td>}
                <td className="p-2 font-mono">{(calc.total_volume_gal || 0).toLocaleString()} gal</td>
              </tr>
            </tbody>
          </table>
        </div>
        {prods.some((p) => !p.rate_in_range) && (
          <div className="mt-2 bg-red-50 border border-red-400 text-red-800 p-2 text-xs font-bold" data-testid="rate-warning">One or more rates are outside the approved label range — verify before mixing.</div>
        )}
        {(calc.compatibility_warnings || []).length > 0 && (
          <div className="mt-2 bg-red-50 border-2 border-red-500 text-red-800 p-3 text-xs space-y-1" data-testid="compat-warnings">
            <div className="font-bold uppercase">Tank-Mix Compatibility</div>
            {calc.compatibility_warnings.map((w, i) => <div key={i} className="font-semibold">• {w}</div>)}
            <div className="text-[10px] text-red-700">Cross-check each label and the CDMS label database before mixing. Jar test any new combination.</div>
          </div>
        )}
      </Panel>

      {/* Products, mixing, PPE */}
      {prods.map((p, i) => (
        <Panel key={p.material_id} title={`${i + 1}. ${p.product || "Product"}${p.material_type && p.material_type !== "Pesticide" ? ` — ${p.material_type}` : ""}`} testId={`product-panel-${i}`}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <Info label={t("fs.active")} v={p.active_ingredient} />
            <Info label={t("fs.epa")} v={p.epa_reg_no} />
            <Info label="FRAC / IRAC" v={[p.frac_group && `FRAC ${p.frac_group}`, p.irac_group && `IRAC ${p.irac_group}`].filter(Boolean).join(" · ") || null} />
            <Info label={t("fs.signal")} v={p.signal_word} />
            <Info label={t("fs.rate")} v={p.rate ? `${p.rate} ${unit(p)}` : "TBD at approval"} />
            <Info label="REI" v={p.rei_hours != null ? `${p.rei_hours} hours` : null} />
            <Info label="PHI" v={p.phi_days != null ? `${p.phi_days} days` : null} />
            <Info label={t("fs.per_tank")} v={p.per_tank != null ? `${p.per_tank} ${amtUnit(p)}` : null} />
          </div>
          {p.mixing_instructions && (
            <div className="mt-2 bg-stone-50 border border-stone-200 p-3 text-sm"><span className="font-bold uppercase text-[10px] text-stone-500 block mb-1">Mixing Instructions</span>{p.mixing_instructions}</div>
          )}
          {(p.ppe_mixer || p.ppe_applicator) && (
            <div className="mt-2 border-2 border-[#1F3D34] p-3 text-sm space-y-1" data-testid={`ppe-panel-${i}`}>
              <div className="font-bold uppercase text-[10px] flex items-center gap-1"><ShieldAlert size={12} /> {t("fs.ppe_req")}</div>
              {p.ppe_mixer && <div><b>Mixer/Loader:</b> {p.ppe_mixer}</div>}
              {p.ppe_applicator && <div><b>Applicator:</b> {p.ppe_applicator}</div>}
            </div>
          )}
          {p.restrictions && <div className="mt-2 text-xs text-red-800 font-semibold">Restrictions: {p.restrictions}</div>}
          {!p.label_complete && <div className="mt-2 text-xs text-amber-700 font-bold">LABEL DATA INCOMPLETE — verify against the registered label before application.</div>}
        </Panel>
      ))}

      {/* PPE Summary */}
      {prods.some((p) => p.ppe_applicator || p.ppe_mixer || p.signal_word) && (
        <Panel title={t("fs.ppe_summary")} testId="ppe-summary">
          <div className="space-y-2 text-sm">
            {prods.map((p, i) => (
              <div key={i} className="border border-stone-200 p-2">
                <div className="font-bold">{p.product} {p.signal_word && <span className={`ml-1 text-[10px] font-bold uppercase px-1.5 py-0.5 ${/danger/i.test(p.signal_word) ? "bg-red-600 text-white" : /warning/i.test(p.signal_word) ? "bg-amber-500 text-white" : "bg-stone-200 text-stone-700"}`}>{p.signal_word}</span>}</div>
                <div className="text-xs"><b>Applicator:</b> {p.ppe_applicator || "Long-sleeved shirt, long pants, shoes plus socks, chemical-resistant gloves (WPS baseline)"}</div>
                <div className="text-xs"><b>Mixer/Loader:</b> {p.ppe_mixer || "Long-sleeved shirt, long pants, shoes plus socks, chemical-resistant gloves (WPS baseline)"}</div>
                <div className="flex gap-3 mt-1">
                  {p.label_url && <a href={p.label_url} target="_blank" rel="noreferrer" className="text-[10px] font-bold underline text-[#1F3D34]">LABEL</a>}
                  {p.sds_url && <a href={p.sds_url} target="_blank" rel="noreferrer" className="text-[10px] font-bold underline text-[#1F3D34]">SDS</a>}
                </div>
              </div>
            ))}
            <div className="bg-[#1F3D34] text-white p-2 text-xs font-bold uppercase">
              Wear the most protective PPE required by any product in this mix · Strictest signal word: {(() => { const sw = prods.map((p) => p.signal_word || "").join(" "); return /danger/i.test(sw) ? "DANGER" : /warning/i.test(sw) ? "WARNING" : "CAUTION"; })()} · REI {maxRei}h
            </div>
          </div>
        </Panel>
      )}

      {/* REI banner */}
      <div className="bg-red-600 text-white p-4" data-testid="rei-banner">
        <div className="font-bold uppercase text-sm">{t("fs.rei_line")}: {maxRei} {t("fs.hours_after")}{maxPhi ? ` · ${t("fs.phi_line")}: ${maxPhi} ${t("fs.days_before")}` : ""}</div>
        <div className="text-xs text-white/80 mt-1">Completing this order starts the REI clock and blocks employee clock-ins on these blocks until it expires.</div>
      </div>

      {/* Weather & precautions */}
      {o.planned_weather && (
        <Panel title={`${t("fs.forecast")} — ${o.planned_weather.date}`} testId="weather-panel">
          <div className="text-sm">{o.planned_weather.temp_min != null && <>Temp {Math.round(o.planned_weather.temp_min)}–{Math.round(o.planned_weather.temp_max)}°F · </>}Wind max {o.planned_weather.wind_max != null ? `${Math.round(o.planned_weather.wind_max)} mph` : "—"} · Rain {o.planned_weather.rain_in}"</div>
          {(o.planned_weather.flags || []).map((fl, i) => <div key={i} className="text-xs text-amber-800 font-semibold mt-1">⚠ {fl}</div>)}
        </Panel>
      )}
      {(o.field_instructions || o.sensitive_areas || o.notes) && (
        <Panel title={t("fs.field_instr")}>
          {o.field_instructions && <div className="text-sm">{o.field_instructions}</div>}
          {o.sensitive_areas && <div className="text-sm mt-1 text-red-800 font-semibold">Sensitive areas: {o.sensitive_areas}</div>}
          {o.notes && <div className="text-xs text-stone-600 mt-1">{o.notes}</div>}
        </Panel>
      )}

      {/* Completion record */}
      {o.actual && (
        <Panel title={t("fs.app_record")} testId="actual-panel">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <Info label={t("fs.date")} v={o.actual.actual_date} />
            <Info label={t("fs.start_end")} v={[o.actual.start_time?.slice(11, 16), o.actual.end_time?.slice(11, 16)].filter(Boolean).join(" – ") || null} />
            <Info label={t("fs.acres")} v={o.actual.actual_acres} />
            <Info label={t("fs.tanks_used")} v={o.actual.actual_tanks} />
            <Info label={t("fs.actual_gpa")} v={o.actual.actual_gpa} />
            <Info label={t("fs.applicator")} v={o.actual.applicator} />
            <Info label={t("fs.weather")} v={o.actual.weather_at_application} />
            <Info label={t("fs.completed_by")} v={o.actual.completed_by} />
          </div>
          {o.actual.deviations && <div className="text-xs text-amber-800 font-semibold mt-2">Deviations: {o.actual.deviations}</div>}
          {o.actual.comments && <div className="text-xs text-stone-600 mt-1">{o.actual.comments}</div>}
        </Panel>
      )}

      {/* Sign-off trail */}
      <Panel title={t("fs.signoff")}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Info label={t("fs.prepared_by")} v={o.prepared_by} />
          <Info label={t("fs.reviewed_by")} v={o.reviewed_by} />
          <Info label={t("fs.approved_by")} v={o.approved_by} />
          <Info label={t("fs.ack_by")} v={o.acknowledged_by} />
        </div>
      </Panel>

      {err && <div className="no-print bg-red-50 border border-red-400 text-red-800 p-3 text-sm font-semibold" data-testid="sheet-error">{err}</div>}

      {/* Actions */}
      <div className="no-print space-y-2 pb-6">
        {o.status === "draft" && isManager && (
          <button data-testid="review-btn" onClick={() => act("review")} disabled={busy} className="w-full h-12 border-2 border-[#1F3D34] bg-white text-[#1F3D34] font-bold uppercase disabled:opacity-40 flex items-center justify-center gap-2"><Eye size={16} /> Mark Reviewed</button>
        )}
        {["draft", "reviewed", "recommended"].includes(o.status) && isManager && (
          <button data-testid="approve-btn" onClick={() => setApproveOpen(true)} disabled={busy} className="w-full h-14 bg-[#1F3D34] text-white font-bold uppercase disabled:opacity-40 flex items-center justify-center gap-2"><ThumbsUp size={16} /> Approve & Release To Field</button>
        )}
        {o.status === "approved" && !o.acknowledged_by && (
          <button data-testid="acknowledge-btn" onClick={() => act("acknowledge")} disabled={busy} className="w-full h-12 border-2 border-[#7C9673] bg-white text-[#1F3D34] font-bold uppercase disabled:opacity-40">I Have Read This Field Sheet</button>
        )}
        {o.status === "approved" && (
          <button data-testid="complete-spray-btn" onClick={() => setCompleteOpen(true)} disabled={busy} className="w-full h-14 bg-[#7C9673] text-white font-bold uppercase disabled:opacity-40 flex items-center justify-center gap-2"><CheckCircle2 size={16} /> Complete Spray</button>
        )}
        {!["completed", "cancelled"].includes(o.status) && isManager && (
          <button data-testid="cancel-order-btn" onClick={() => act("cancel")} disabled={busy} className="w-full h-11 border border-red-300 text-red-700 font-bold uppercase text-xs disabled:opacity-40 flex items-center justify-center gap-2"><Ban size={14} /> Cancel Order</button>
        )}
      </div>

      {completeOpen && <CompleteModal order={o} onClose={() => setCompleteOpen(false)} onDone={(d) => { setO(d); setCompleteOpen(false); }} />}
      {approveOpen && <ApproveModal order={o} onClose={() => setApproveOpen(false)} onDone={(d) => { setO(d); setApproveOpen(false); }} />}
    </div>
  );
}

function Panel({ title, children, testId }) {
  return (
    <div className="bg-white border border-stone-200 p-4" data-testid={testId}>
      <div className="font-bold uppercase tracking-wide text-sm mb-2">{title}</div>
      {children}
    </div>
  );
}

function Info({ label, v, dark }) {
  return (
    <div>
      <div className={`text-[10px] font-bold uppercase ${dark ? "text-white/50" : "text-stone-400"}`}>{label}</div>
      <div className={`font-semibold ${dark ? "" : "text-stone-800"}`}>{v ?? "—"}</div>
    </div>
  );
}

function ApproveModal({ order, onClose, onDone }) {
  const [materials, setMaterials] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [tank, setTank] = useState(String(order.tank_gal || 500));
  const [gpa, setGpa] = useState(String(order.gpa || 100));
  const [pattern, setPattern] = useState(order.pattern || "every_row");
  const [startRow, setStartRow] = useState(order.start_row || "");
  const [direction, setDirection] = useState(order.direction || "");
  const [date, setDate] = useState(order.date || "");
  const [sel, setSel] = useState((order.products || []).map((p) => ({ material_id: p.material_id, rate: p.rate ? String(p.rate) : "", rate_unit: p.rate_unit || "" })));
  const [lastFrac, setLastFrac] = useState(null);
  useEffect(() => {
    api.get(`/pest/applications?ranch_number=${order.ranch_number}`).then(({ data }) => {
      const apps = data
        .filter((a) => (a.block_ids || []).some((b) => (order.block_ids || []).includes(b)) && a.frac_group)
        .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      if (apps[0]) setLastFrac({ frac: String(apps[0].frac_group).trim(), product: apps[0].product, date: apps[0].date });
    }).catch(() => {});
  }, []); // eslint-disable-line
  const MULTI_SITE = ["M01", "M02", "M", "NC"];
  const repeatsFrac = (m) => !!(lastFrac && m?.frac_group && String(m.frac_group).trim() === lastFrac.frac && !MULTI_SITE.includes(lastFrac.frac));
  useEffect(() => { api.get("/materials").then(({ data }) => setMaterials(data.filter((m) => m.active))); }, []);
  const recs = order.recommended_materials || [];
  const recIds = new Set(recs.map((m) => m.id));
  const matById = (mid) => materials.find((m) => m.id === mid) || recs.find((m) => m.id === mid) || {};
  const selMats = sel.map((x) => matById(x.material_id)).filter((m) => m && m.id);
  const compat = compatWarnings(selMats);
  const rangeIssues = sel.filter((x) => rateOutOfRange(matById(x.material_id), x.rate));
  const isSel = (mid) => sel.some((x) => x.material_id === mid);
  const toggle = (m) => setSel((s) => isSel(m.id) ? s.filter((x) => x.material_id !== m.id) : [...s, { material_id: m.id, rate: "", rate_unit: m.rate_unit || "" }]);
  const setRate = (mid, rate) => setSel((s) => s.map((x) => (x.material_id === mid ? { ...x, rate } : x)));
  const valid = Number(tank) > 0 && Number(gpa) > 0 && sel.length > 0 && sel.every((x) => Number(x.rate) > 0)
    && rangeIssues.length === 0
    && (pattern !== "every_other_row" || startRow.trim());
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/pest/spray-orders/${order.id}/approve`, {
        tank_gal: Number(tank), gpa: Number(gpa), pattern,
        start_row: startRow || null, direction: direction || null, date: date || null,
        products: sel.map((x) => ({ material_id: x.material_id, rate: Number(x.rate), rate_unit: x.rate_unit || matById(x.material_id).rate_unit || "" })),
      });
      onDone(data);
    } catch (e) { setErr(e.response?.data?.detail || "Could not approve the order"); }
    finally { setBusy(false); }
  };
  const MatCard = ({ m, recommended }) => (
    <div className={`border p-3 space-y-2 ${isSel(m.id) ? "border-[#1F3D34] border-2 bg-[#7C9673]/5" : "border-stone-200"}`} data-testid={`approve-mat-${m.id}`}>
      <div className="flex items-start gap-2">
        <input type="checkbox" checked={isSel(m.id)} onChange={() => toggle(m)} className="w-5 h-5 mt-0.5 accent-[#1F3D34]" data-testid={`approve-mat-check-${m.id}`} />
        <div className="min-w-0 flex-1">
          <div className="font-bold text-sm flex flex-wrap items-center gap-1.5">
            {m.product}
            {recommended && m.activity && (
              <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 ${m.activity.includes("Curative") && m.activity.includes("Preventative") ? "bg-[#1F3D34] text-white" : m.activity === "Curative" ? "bg-red-600 text-white" : "bg-[#7C9673] text-white"}`}>{m.activity}</span>
            )}
            {recommended && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 bg-amber-100 text-amber-800">Recommended</span>}
          </div>
          <div className="text-xs text-stone-600">
            {m.active_ingredient || "AI not entered"}{m.frac_group ? ` · FRAC ${m.frac_group}` : ""}{m.irac_group ? ` · IRAC ${m.irac_group}` : ""}
            {" · "}REI {m.rei_hours ?? "—"}h · PHI {m.phi_days ?? "—"}d
            {m.supplier ? ` · ${m.supplier}` : ""}
          </div>
          {m.rate_min != null && m.rate_max != null ? (
            <div className="text-xs font-bold text-[#1F3D34]" data-testid={`label-range-${m.id}`}>Label rate (wine grapes): {m.rate_min}–{m.rate_max} {m.rate_unit || ""}</div>
          ) : (
            <div className="text-xs font-bold text-amber-700" data-testid={`label-range-missing-${m.id}`}>No verified label range — confirm on <a href={labelSearchUrl(m.product)} target="_blank" rel="noreferrer" className="underline">CDMS</a> before approving</div>
          )}
        </div>
      </div>
      {isSel(m.id) && (
        <>
          <input data-testid={`approve-rate-${m.id}`} value={sel.find((x) => x.material_id === m.id)?.rate || ""}
            onChange={(e) => setRate(m.id, e.target.value)}
            placeholder={m.rate_min != null ? `Rate (${m.rate_min}–${m.rate_max} ${m.rate_unit || ""})` : `Rate ${m.rate_unit ? `(${m.rate_unit})` : ""}`}
            className={`w-full h-11 px-3 border text-sm ${rateOutOfRange(m, sel.find((x) => x.material_id === m.id)?.rate) ? "border-red-500 border-2 bg-red-50" : "border-stone-300"}`} />
          {rateOutOfRange(m, sel.find((x) => x.material_id === m.id)?.rate) && (
            <div className="text-xs text-red-700 font-bold" data-testid={`rate-range-error-${m.id}`}>
              Outside the label range {m.rate_min}–{m.rate_max} {m.rate_unit || ""} for wine grapes — enter a rate within range to approve.
            </div>
          )}
        </>
      )}
      {repeatsFrac(m) && (
        <div className="text-xs text-red-700 font-bold" data-testid={`frac-warning-${m.id}`}>
          Repeats FRAC {lastFrac.frac} — last applied {lastFrac.date} ({lastFrac.product}). Rotate mode of action.
        </div>
      )}
    </div>
  );
  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white border border-stone-200 w-full max-w-lg p-5 space-y-4 max-h-[92vh] overflow-y-auto" data-testid="approve-modal">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-black uppercase" style={{ fontFamily: "'Fraunces', serif" }}>Approve {order.wo_number}</h3>
          <button onClick={onClose} data-testid="approve-close"><X size={20} /></button>
        </div>

        <Field label="Row Pattern">
          <div className="flex flex-wrap gap-2" data-testid="approve-pattern-chips">
            {[["every_row", "Every Row"], ["every_other_row", "Every Other Row"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`approve-pattern-${v}`} onClick={() => setPattern(v)}
                className={`h-11 px-4 text-sm font-semibold border ${pattern === v ? "bg-[#1F3D34] text-white border-[#1F3D34]" : "bg-white border-stone-300"}`}>{l}</button>
            ))}
          </div>
        </Field>
        {pattern === "every_other_row" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start Row (required)"><input data-testid="approve-start-row" value={startRow} onChange={(e) => setStartRow(e.target.value)} className="w-full h-12 px-3 border border-stone-300" placeholder="e.g. Row 1" /></Field>
            <Field label="Direction"><input value={direction} onChange={(e) => setDirection(e.target.value)} className="w-full h-12 px-3 border border-stone-300" placeholder="odd rows, N→S" /></Field>
          </div>
        )}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Tank Size (gal)"><input data-testid="approve-tank-input" value={tank} onChange={(e) => setTank(e.target.value)} className="w-full h-12 px-3 border border-stone-300" /></Field>
          <Field label="GPA"><input data-testid="approve-gpa-input" value={gpa} onChange={(e) => setGpa(e.target.value)} className="w-full h-12 px-3 border border-stone-300" /></Field>
          <Field label="Spray Date"><input type="date" data-testid="approve-date-input" value={date} onChange={(e) => setDate(e.target.value)} className="w-full h-12 px-3 border border-stone-300" /></Field>
        </div>

        {recs.length > 0 && (
          <Field label={`Recommended Materials for ${order.target}`}>
            <div className="space-y-2" data-testid="approve-recommended-list">
              {recs.map((m) => <MatCard key={m.id} m={m} recommended />)}
            </div>
          </Field>
        )}
        {recs.length === 0 && order.status === "recommended" && (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-300 p-2.5 font-semibold">No approved materials list {order.target} as a target yet — add targets on the Approved Materials page, or pick from all materials below.</div>
        )}
        <Field label="All Approved Materials">
          <select data-testid="approve-add-material" value="" onChange={(e) => { const m = materials.find((x) => x.id === e.target.value); if (m && !isSel(m.id)) toggle(m); }}
            className="w-full h-12 px-3 border border-stone-300">
            <option value="">— Add another material —</option>
            {materials.filter((m) => !recIds.has(m.id) && !isSel(m.id)).map((m) => (
              <option key={m.id} value={m.id}>{m.product}{m.material_type && m.material_type !== "Pesticide" ? ` · ${m.material_type}` : ""}{m.activity ? ` · ${m.activity}` : ""}{m.frac_group ? ` · FRAC ${m.frac_group}` : ""}</option>
            ))}
          </select>
        </Field>
        {sel.filter((x) => !recIds.has(x.material_id)).map((x) => <MatCard key={x.material_id} m={matById(x.material_id)} recommended={false} />)}

        {Number(gpa) > 0 && Number(tank) > 0 && (
          <div className="bg-stone-50 border border-stone-200 p-3 text-xs" data-testid="approve-calc-preview">
            {(pattern === "every_other_row" ? Math.round((order.block_acres || 0) * 0.5 * 10) / 10 : order.block_acres)} ac treated × {gpa} GPA
            {" = "}<b>{Math.round(Number(gpa) * (pattern === "every_other_row" ? (order.block_acres || 0) * 0.5 : order.block_acres || 0)).toLocaleString()} gal</b>
            {" · "}{Math.floor((Number(gpa) * (pattern === "every_other_row" ? (order.block_acres || 0) * 0.5 : order.block_acres || 0)) / Number(tank))} full {tank}-gal tanks
          </div>
        )}

        {err && <div className="bg-red-50 border border-red-400 text-red-800 p-2.5 text-xs font-semibold" data-testid="approve-error">{err}</div>}
        {compat.length > 0 && (
          <div className="bg-red-50 border-2 border-red-500 text-red-800 p-3 text-xs space-y-1" data-testid="approve-compat-warning">
            <div className="font-bold uppercase">Tank-Mix Compatibility</div>
            {compat.map((w, i) => <div key={i} className="font-semibold">• {w}</div>)}
            <div className="text-[10px]">Cross-check in the <a href={LABEL_DB_URL} target="_blank" rel="noreferrer" className="underline font-bold">CDMS label database</a> and each product label. Jar test any new combination.</div>
          </div>
        )}
        {sel.some((x) => repeatsFrac(matById(x.material_id))) && (
          <div className="bg-red-50 border-2 border-red-500 text-red-800 p-3 text-xs font-bold" data-testid="frac-rotation-warning">
            FRAC ROTATION WARNING — a selected material repeats FRAC {lastFrac?.frac}, the group last applied on these blocks ({lastFrac?.date} · {lastFrac?.product}). Repeating a single-site mode of action increases resistance risk. You can still approve if the label and your PCA allow it.
          </div>
        )}
        <button data-testid="approve-submit-btn" onClick={submit} disabled={busy || !valid}
          className="w-full h-14 bg-[#1F3D34] text-white font-bold uppercase disabled:opacity-40 flex items-center justify-center gap-2">
          {busy && <Loader2 size={16} className="animate-spin" />} Approve & Release To Field
        </button>
        <div className="text-[10px] text-stone-500">Tank math recalculates with your selections. The pesticide label is controlling — verify rates before mixing.</div>
      </div>
    </div>
  );
}

function CompleteModal({ order, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [f, setF] = useState({
    actual_date: new Date().toISOString().slice(0, 10), start_time: "", end_time: "",
    actual_acres: "", actual_tanks: "", actual_gpa: "", weather_at_application: "",
    deviations: "", comments: "", applicator: order.applicator || "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const num = (v) => (v === "" ? null : Number(v));
      const { data } = await api.post(`/pest/spray-orders/${order.id}/complete`, {
        actual_date: f.actual_date,
        start_time: f.start_time ? `${f.actual_date}T${f.start_time}:00` : null,
        end_time: f.end_time ? `${f.actual_date}T${f.end_time}:00` : null,
        actual_acres: num(f.actual_acres), actual_tanks: num(f.actual_tanks), actual_gpa: num(f.actual_gpa),
        weather_at_application: f.weather_at_application || null,
        deviations: f.deviations || null, comments: f.comments || null, applicator: f.applicator || null,
      });
      onDone(data);
    } catch (e) { setErr(e.response?.data?.detail || "Could not complete the order"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white border border-stone-200 w-full max-w-md p-5 space-y-3 max-h-[92vh] overflow-y-auto" data-testid="complete-modal">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-black uppercase" style={{ fontFamily: "'Fraunces', serif" }}>Complete {order.wo_number}</h3>
          <button onClick={onClose} data-testid="complete-close"><X size={20} /></button>
        </div>
        <div className="bg-red-50 border border-red-300 text-red-800 p-2.5 text-xs font-semibold">This records a verified application and starts the REI clock on the sprayed blocks.</div>
        <Field label="Application Date"><input type="date" data-testid="complete-date-input" value={f.actual_date} onChange={(e) => set("actual_date", e.target.value)} className="w-full h-12 px-3 border border-stone-300" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start (optional)"><input type="time" value={f.start_time} onChange={(e) => set("start_time", e.target.value)} className="w-full h-12 px-3 border border-stone-300" /></Field>
          <Field label="Finish (optional)"><input type="time" data-testid="complete-end-input" value={f.end_time} onChange={(e) => set("end_time", e.target.value)} className="w-full h-12 px-3 border border-stone-300" /></Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Acres"><input value={f.actual_acres} onChange={(e) => set("actual_acres", e.target.value)} placeholder={String(order.treated_acres || "")} className="w-full h-12 px-3 border border-stone-300" /></Field>
          <Field label="Tanks"><input value={f.actual_tanks} onChange={(e) => set("actual_tanks", e.target.value)} className="w-full h-12 px-3 border border-stone-300" /></Field>
          <Field label="GPA"><input value={f.actual_gpa} onChange={(e) => set("actual_gpa", e.target.value)} placeholder={String(order.gpa || "")} className="w-full h-12 px-3 border border-stone-300" /></Field>
        </div>
        <Field label="Weather / Wind (optional)"><input value={f.weather_at_application} onChange={(e) => set("weather_at_application", e.target.value)} placeholder="e.g. 62°F, wind 4 mph NW" className="w-full h-12 px-3 border border-stone-300" /></Field>
        <Field label="Deviations From Plan (optional)"><input value={f.deviations} onChange={(e) => set("deviations", e.target.value)} className="w-full h-12 px-3 border border-stone-300" /></Field>
        <Field label="Comments (optional)"><input value={f.comments} onChange={(e) => set("comments", e.target.value)} className="w-full h-12 px-3 border border-stone-300" /></Field>
        <Field label="Applicator"><input value={f.applicator} onChange={(e) => set("applicator", e.target.value)} className="w-full h-12 px-3 border border-stone-300" /></Field>
        {err && <div className="bg-red-50 border border-red-400 text-red-800 p-2 text-xs font-semibold" data-testid="complete-error">{err}</div>}
        <button data-testid="complete-submit-btn" onClick={submit} disabled={busy || !f.actual_date}
          className="w-full h-14 bg-[#1F3D34] text-white font-bold uppercase disabled:opacity-40 flex items-center justify-center gap-2">
          {busy && <Loader2 size={16} className="animate-spin" />} Record Application & Start REI
        </button>
      </div>
    </div>
  );
}
